import { FC } from "react";
import {
    Button,
    FormControl,
    FormLabel,
    HStack,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    VStack,
} from "@chakra-ui/react";
import { FieldValues, SubmitHandler, useForm } from "react-hook-form";
import { DatePicker, SingleSelectControlled, useToast } from "@am-tax/fe-core";
import { displayEmploymentStatus, EmploymentStatus, EntityCandidateForUpdate } from "../../api/models/EntityCandidate";
import { useCandidatePatch } from "../../api/hooks/useCandidatePatch";
import { buildJsonPatch } from "../../utils/buildJsonPatch";

interface Props {
    isOpen: boolean;
    onClose: () => void;
    candidate: Partial<EntityCandidateForUpdate>;
    cId: string | undefined;
    eId: string | undefined;
    candidateId: string | undefined;
}

type FormValues = Partial<EntityCandidateForUpdate> & FieldValues;

export const CandidateEmploymentModal: FC<Props> = ({ isOpen, onClose, candidate, cId, eId, candidateId }) => {
    const toast = useToast();
    const patchCandidate = useCandidatePatch();

    const {
        register,
        handleSubmit,
        control,
        formState: { dirtyFields },
        reset,
    } = useForm<FormValues>({ values: candidate as FormValues });

    const handleClose = () => {
        reset();
        onClose();
    };

    const onSubmit: SubmitHandler<FormValues> = async data => {
        const ops = buildJsonPatch(dirtyFields as Record<string, unknown>, data as Record<string, unknown>);
        if (ops.length > 0) {
            await patchCandidate.mutateAsync({ clientId: cId, entityId: eId, candidateId, patch: ops });
        }
        toast({ title: "Saved", status: "success", duration: 3000, isClosable: true });
        handleClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} size={"xl"}>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Edit Employment</ModalHeader>
                <ModalCloseButton />
                <form onSubmit={handleSubmit(onSubmit)}>
                    <ModalBody>
                        <fieldset disabled={patchCandidate.isLoading}>
                            <VStack gap={"1rem"} alignItems={"start"}>
                                <FormControl>
                                    <FormLabel>Employment Status:</FormLabel>
                                    <SingleSelectControlled
                                        control={control}
                                        name="employmentStatus"
                                        placeholder={"Choose Employment Status"}
                                        options={Object.values(EmploymentStatus).map(s => ({
                                            label: displayEmploymentStatus(s),
                                            value: s,
                                        }))}
                                    />
                                </FormControl>

                                <FormControl>
                                    <FormLabel>Job Title:</FormLabel>
                                    <Input type="text" {...register("employmentJobTitle")} />
                                </FormControl>

                                <HStack width={"100%"} gap={"1rem"}>
                                    <FormControl>
                                        <FormLabel>Hire Date:</FormLabel>
                                        <DatePicker {...register("employmentHireDate")} />
                                    </FormControl>
                                    <FormControl>
                                        <FormLabel>Start Date:</FormLabel>
                                        <DatePicker {...register("employmentStartDate")} />
                                    </FormControl>
                                </HStack>
                                <HStack width={"100%"} gap={"1rem"}>
                                    <FormControl>
                                        <FormLabel>Offer Date:</FormLabel>
                                        <DatePicker {...register("employmentOfferDate")} />
                                    </FormControl>
                                    <FormControl>
                                        <FormLabel>Termination Date:</FormLabel>
                                        <DatePicker {...register("employmentTerminationDate")} />
                                    </FormControl>
                                </HStack>
                            </VStack>
                        </fieldset>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            type="submit"
                            variant={"primary"}
                            mr={3}
                            isLoading={patchCandidate.isLoading}
                            isDisabled={patchCandidate.isLoading}
                            loadingText={"Saving"}
                        >
                            Save
                        </Button>
                        <Button variant="ghost" onClick={handleClose}>
                            Cancel
                        </Button>
                    </ModalFooter>
                </form>
            </ModalContent>
        </Modal>
    );
};
